import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { tournamentApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import toast from 'react-hot-toast';

const SPORTS = [
  { value: 'all', label: 'All Sports' },
  { value: 'badminton', label: 'Badminton' },
  { value: 'tennis', label: 'Tennis' },
  { value: 'table_tennis', label: 'Table Tennis' },
  { value: 'squash', label: 'Squash' },
  { value: 'pickleball', label: 'Pickleball' },
  { value: 'padel', label: 'Padel' },
];

const SPORT_ICONS = {
  badminton: '🏸', tennis: '🎾', table_tennis: '🏓',
  squash: '🟡', pickleball: '🥒', padel: '🎾',
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'registration', label: 'Open' },
  { value: 'in_progress', label: 'Live' },
  { value: 'completed', label: 'Completed' },
];

const TournamentList = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sportFilter, setSportFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadTournaments, searchQuery ? 300 : 0);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filter, searchQuery, sportFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const parseJsonField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
      try { const p = JSON.parse(field); return Array.isArray(p) ? p : []; }
      catch { return []; }
    }
    return [];
  };

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (sportFilter !== 'all') params.sport_type = sportFilter;
      if (searchQuery.trim()) params.q = searchQuery.trim();
      const response = await tournamentApi.search(params);
      setTournaments(response.data.map(t => ({
        ...t,
        event_type: parseJsonField(t.event_type),
        event_category: parseJsonField(t.event_category),
        event_age_category: parseJsonField(t.event_age_category),
      })));
    } catch (err) {
      setError('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    const map = {
      draft:        { background: '#f3f4f6', color: '#9ca3af' },
      registration: { background: '#F0FDFA', color: '#0D9488' },
      seeding:      { background: '#F0FDFA', color: '#0F766E' },
      in_progress:  { background: '#ecfdf5', color: '#059669' },
      completed:    { background: '#f3f4f6', color: '#6b7280' },
      cancelled:    { background: '#fef2f2', color: '#dc2626' },
    };
    return map[status] || map.draft;
  };

  const toInitCap = (str) =>
    str ? str.split(/[\s_]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  // Routes to the management view for owners, admins, and invited staff
  const canManage = (t) => user && (
    user.role === 'admin' ||
    t.created_by === user.id ||
    ['moderator', 'umpire'].includes(user.role)
  );
  // Only the tournament creator or admin can delete/duplicate
  const canDelete = (t) => user && (user.role === 'admin' || t.created_by === user.id);
  const canDuplicate = (t) => user && (user.role === 'admin' || t.created_by === user.id);

  const handleDuplicate = async (tournamentId) => {
    try {
      const res = await tournamentApi.duplicate(tournamentId);
      toast.success('Tournament duplicated');
      setTournaments(prev => [{ ...res.data, event_type: [], event_category: [], event_age_category: [] }, ...prev]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to duplicate tournament');
    }
  };

  const handleDelete = async (tournamentId) => {
    if (!await confirm({ title: 'Delete Tournament', message: 'Delete this tournament? This cannot be undone.', confirmLabel: 'Delete', danger: true })) return;
    setDeleting(tournamentId);
    try {
      await tournamentApi.delete(tournamentId);
      setTournaments(prev => prev.filter(t => t.id !== tournamentId));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete tournament');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="container">
      {/* Page header — compact */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1.5rem 0 1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#111827', margin: 0 }}>Tournaments</h1>
          <p style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: 3 }}>
            {loading ? '' : `${tournaments.length} tournament${tournaments.length !== 1 ? 's' : ''} found`}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem' }}>

        {/* Search — full width */}
        <div style={{ position: 'relative' }}>
          <svg
            width="14" height="14" viewBox="0 0 20 20" fill="none"
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}
          >
            <path d="M17 17l-3.5-3.5M14.5 8.5a6 6 0 11-12 0 6 6 0 0112 0z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search tournaments..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%', height: 36, paddingLeft: 30, paddingRight: 10,
              fontSize: '0.85rem', border: '1px solid #e5e7eb', borderRadius: 8,
              background: 'white', color: '#111827', outline: 'none',
              fontFamily: 'inherit',
            }}
            onFocus={e => { e.target.style.borderColor = '#5EEAD4'; e.target.style.boxShadow = '0 0 0 3px rgba(13,148,136,0.12)'; }}
            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
          />
        </div>

        {/* Sport + Status — second row */}
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={sportFilter}
            onChange={e => setSportFilter(e.target.value)}
            style={{
              height: 34, padding: '0 10px', fontSize: '0.82rem',
              border: '1px solid #e5e7eb', borderRadius: 8,
              background: 'white', color: '#374151', outline: 'none',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {SPORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <div style={{ display: 'flex', gap: '3px', background: '#f3f4f6', padding: '3px', borderRadius: 8 }}>
            {STATUS_FILTERS.map(s => (
              <button
                key={s.value}
                onClick={() => setFilter(s.value)}
                style={{
                  padding: '4px 12px', fontSize: '0.8rem', fontWeight: 500,
                  borderRadius: 6, border: 'none', cursor: 'pointer',
                  transition: 'all 0.12s',
                  background: filter === s.value ? 'white' : 'transparent',
                  color: filter === s.value ? '#0D9488' : '#6b7280',
                  boxShadow: filter === s.value ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.25rem' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ background: 'white', border: '1px solid #f0f0f0', borderRadius: 14, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.75rem' }}>
                <div className="skeleton" style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="skeleton" style={{ height: 14, width: '70%', marginBottom: 6 }} />
                  <div className="skeleton" style={{ height: 11, width: '45%' }} />
                </div>
                <div className="skeleton" style={{ height: 20, width: 60, borderRadius: 999 }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '0.75rem' }}>
                <div className="skeleton" style={{ height: 11, width: '55%' }} />
                <div className="skeleton" style={{ height: 11, width: '40%' }} />
                <div className="skeleton" style={{ height: 11, width: '35%' }} />
              </div>
              <div className="skeleton" style={{ height: 34, borderRadius: 8 }} />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="alert alert-error">{error}</div>
      ) : tournaments.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span className="empty-state-icon">🏆</span>
            <h3 style={{ fontWeight: 600, marginBottom: '0.4rem', color: '#374151' }}>No tournaments found</h3>
            <p style={{ fontSize: '0.875rem' }}>
              {searchQuery || sportFilter !== 'all' ? 'Try adjusting your filters.' : 'No tournaments yet. Check back later.'}
            </p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.25rem' }}>
          {tournaments.map((tournament) => {
            const statusStyle = getStatusStyle(tournament.status);
            const sportIcon = SPORT_ICONS[tournament.sport_type] || '🏆';
            const tags = [
              ...tournament.event_type?.slice(0, 2).map(toInitCap) || [],
              ...tournament.event_category?.slice(0, 1).map(toInitCap) || [],
              ...tournament.event_age_category?.slice(0, 1).map(toInitCap) || [],
            ].filter(Boolean);

            return (
              <div
                key={tournament.id}
                className="tournament-card"
                style={{
                  background: 'white', border: '1px solid #f0f0f0', borderRadius: 14,
                  padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  display: 'flex', flexDirection: 'column',
                }}
              >
                {/* Top row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        width: 40, height: 40, background: '#F0FDFA', borderRadius: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1.2rem', flexShrink: 0,
                      }}
                    >
                      {sportIcon}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111827', lineHeight: 1.2 }}>
                        {tournament.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2 }}>
                        {toInitCap(tournament.sport_type)} · {toInitCap(tournament.draw_type)}
                      </div>
                    </div>
                  </div>
                  <span
                    style={{
                      ...statusStyle, padding: '3px 9px', borderRadius: 999,
                      fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase',
                      letterSpacing: '0.05em', flexShrink: 0,
                    }}
                  >
                    {tournament.status.replace('_', ' ')}
                  </span>
                </div>

                {/* Info rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: '0.75rem' }}>
                  {tournament.start_date && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#6b7280' }}>
                      <span>📅</span>
                      <span>{formatDate(tournament.start_date)}{tournament.end_date && tournament.end_date !== tournament.start_date ? ` — ${formatDate(tournament.end_date)}` : ''}</span>
                    </div>
                  )}
                  {tournament.venue && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#6b7280' }}>
                      <span>📍</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tournament.venue}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#6b7280' }}>
                    <span>👥</span>
                    <span>{tournament.player_count || 0} players</span>
                  </div>
                </div>

                {/* Tags */}
                {tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: '0.75rem' }}>
                    {tags.map(tag => (
                      <span
                        key={tag}
                        style={{
                          background: '#f3f4f6', color: '#6b7280', padding: '2px 8px',
                          borderRadius: 999, fontSize: '0.7rem', fontWeight: 500,
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                  <Link
                    to={canManage(tournament) ? `/tournaments/${tournament.id}` : `/t/${tournament.id}`}
                    className="btn btn-primary btn-sm"
                    style={{ flex: 1, justifyContent: 'center' }}
                  >
                    View Details
                  </Link>
                  {canDuplicate(tournament) && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleDuplicate(tournament.id)}
                      title="Duplicate Tournament"
                    >
                      ⧉
                    </button>
                  )}
                  {canDelete(tournament) && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleDelete(tournament.id)}
                      disabled={deleting === tournament.id}
                      title="Delete Tournament"
                      style={{ color: '#dc2626', borderColor: '#fecaca' }}
                    >
                      {deleting === tournament.id ? '...' : '🗑️'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TournamentList;
